import {
	faBroadcastTower,
	faBug,
	faBuilding,
	faCalendar,
	faCar,
	faCarCrash,
	faClipboardList,
	faDollarSign,
	faExchangeAlt,
	faFile,
	faFileAlt,
	faFileInvoiceDollar,
	faHome,
	faSearch,
	faSitemap,
	faTachometerAlt,
} from "@fortawesome/free-solid-svg-icons"

// ─────────────────────────────────────────────────────────────────────────────
// Source de vérité UNIQUE de la navigation (top-bar + dashboard).
//
// `NAV_ITEMS` = registre : pour chaque destination, ses métadonnées canoniques
// (libellé, icône, route, droit requis, `kind` pour les items dynamiques). Nav,
// dashboard ET gardes de route s'appuient dessus → destinations et droits ne
// peuvent plus diverger.
//
// `NAV_LAYOUT` / `DASHBOARD_LAYOUT` = structure ORDONNÉE propre à chaque surface
// (les deux divergent réellement : ordre, sous-ensembles, quelques icônes/params).
// Une entrée de layout est soit une clé (string), soit `{ key, ...overrides }`
// (override d'icône, de `from`… pour rester fidèle au comportement existant).
//
// Les comportements dynamiques (fiche ouverte, disabled, modal master, badge)
// sont résolus par chaque renderer via le `kind`.
// ─────────────────────────────────────────────────────────────────────────────

export const NAV_ITEMS = {
	// Tableau de bord
	home: { label: "Menu", icon: faHome, to: "/home", exact: true },
	parc: { label: "Parc", icon: faCar, to: "/parc", exact: true },
	stats: {
		label: "Stats",
		icon: faTachometerAlt,
		to: "/stats",
		exact: true,
		right: "stats",
	},
	configuration: {
		label: "Configuration",
		icon: faBuilding,
		to: "/configuration",
		right: "configuration",
	},
	encaissements: {
		label: "Encaissements",
		icon: faDollarSign,
		to: "/encaissements",
		right: "encaissements",
	},
	facturation: {
		label: "Facturation",
		icon: faFileInvoiceDollar,
		to: "/facturation",
		right: "facturation",
	},
	rapprochement: {
		label: "Rapprochement",
		icon: faExchangeAlt,
		to: "/rapprochement",
		right: "encaissements",
	},
	erreurs: {
		label: "Erreurs",
		icon: faBug,
		to: "/erreurs",
		right: "erreurs",
	},
	patchnotes: {
		label: "Patchnotes",
		icon: faClipboardList,
		to: "/patchnotes",
		right: "patchnotes",
	},

	// Entrées directes
	agenda: { label: "Agenda", to: "/agenda", right: "agenda" },
	clients: { label: "Clients", to: "/clients", right: "clients" },
	entretiens: {
		label: "Entretiens",
		to: "/entretiens",
		right: "entretiens",
		badgeKey: "entretiensRetard",
	},
	taches: { label: "Tâches", to: "/taches", right: "taches" },

	// Dépannage
	depDispatch: {
		label: "Dispatch",
		icon: faBroadcastTower,
		to: "/depannage/dispatch",
		right: "dep_dispatch",
	},
	depFiche: { label: "Fiche", icon: faFile, kind: "fiche", domain: "dep" },
	depSearch: {
		label: "Rechercher",
		icon: faSearch,
		to: "/depannage/rechercher",
	},
	// GE-46 : badge = fiches à compléter. A9 : module `dep_etat` (mêmes groupes
	// que les menus serveur DEPANNAGE_STATE_MENUS).
	depEtat: {
		label: "Etat",
		right: "dep_etat",
		icon: faCarCrash,
		to: "/depannage/etat",
		badgeKey: "depACompleter",
	},

	// Atelier
	atePassage: {
		label: "Fiche Client de Passage",
		icon: faFile,
		kind: "passage",
		right: "atelier",
	},
	ateFiche: {
		label: "Fiche",
		icon: faFile,
		kind: "fiche",
		domain: "ate",
		right: "atelier",
	},
	ateMaster: {
		label: "Fiche master",
		icon: faFileAlt,
		kind: "master",
		right: "atelier",
	},
	// Décision Jordan (31.07.2026) : « Rechercher » est TOUJOURS disponible,
	// pour tous les modules — ni grisé (rework verrouillée/libre), ni masqué
	// par un droit (alignement sur depSearch, ouvert depuis toujours). Les
	// routes de recherche/résultats sont ouvertes en conséquence (App.js) ;
	// l'OUVERTURE d'une fiche depuis les résultats reste, elle, gatée par le
	// droit du module.
	ateSearch: {
		label: "Rechercher",
		icon: faSearch,
		to: "/atelier/rechercher",
	},
	ateModeles: {
		label: "Modèles",
		icon: faFileAlt,
		to: "/atelier/modeles",
		right: "atelier_modeles",
	},
	ateEtat: {
		label: "Etat",
		icon: faCarCrash,
		to: "/atelier/etat",
		right: "atelier",
	},
	ateInventaire: {
		label: "Inventaire",
		icon: faSitemap,
		to: "/atelier/inventaire",
		right: "inventaire_page",
		badgeKey: "stockNonDebite",
	},

	// Location
	locFiche: {
		label: "Fiche",
		icon: faFile,
		kind: "fiche",
		domain: "loc",
		right: "location",
	},
	// Même règle que ateSearch : recherche toujours disponible (cf. ci-dessus).
	locSearch: {
		label: "Rechercher",
		icon: faSearch,
		to: "/location/rechercher",
	},
	locGerer: {
		label: "Gérer",
		icon: faCalendar,
		to: "/location/gerer",
		right: "location",
	},
}

// Structure de la barre de navigation (ordre + groupes/directs).
export const NAV_LAYOUT = [
	{
		type: "group",
		key: "dashboard",
		label: "Tableau de bord",
		items: [
			"home",
			"parc",
			"stats",
			"configuration",
			"encaissements",
			"facturation",
			"rapprochement",
			"erreurs",
			"patchnotes",
		],
	},
	{ type: "direct", key: "agenda" },
	{ type: "direct", key: "clients" },
	{
		type: "group",
		key: "depannage",
		label: "Dépannage",
		items: ["depDispatch", "depFiche", "depSearch", "depEtat"],
	},
	{
		type: "group",
		key: "atelier",
		label: "Atelier",
		items: [
			"ateFiche",
			"ateSearch",
			"ateModeles",
			"ateEtat",
			"ateInventaire",
			"ateMaster",
			"atePassage",
		],
	},
	{
		type: "group",
		key: "location",
		label: "Location",
		items: ["locFiche", "locSearch", "locGerer"],
	},
	{ type: "direct", key: "entretiens" },
	{ type: "direct", key: "taches" },
]

// Structure du dashboard (onglets domaine). Uniformisée sur la top-bar
// (décision Jordan 10.08.2026) : mêmes entrées, même ordre, mêmes icônes que
// les groupes Dépannage/Atelier/Location de NAV_LAYOUT. Seul override restant :
// `from` sur Dispatch (comportement de retour, pas d'apparence).
export const DASHBOARD_LAYOUT = [
	{
		label: "Dépannage",
		value: 0,
		items: [
			{ key: "depDispatch", from: "home" },
			"depFiche",
			"depSearch",
			"depEtat",
		],
	},
	{
		label: "Atelier",
		value: -1,
		items: [
			"ateFiche",
			"ateSearch",
			"ateModeles",
			"ateEtat",
			"ateInventaire",
			"ateMaster",
			"atePassage",
		],
	},
	{
		label: "Location",
		value: -2,
		items: ["locFiche", "locSearch", "locGerer"],
	},
]

// Normalise une entrée de layout (string | objet) en `{ ...item, ...overrides }`.
export const resolveLayoutItem = (entry) => {
	const { key, ...overrides } =
		typeof entry === "string" ? { key: entry } : entry
	return { key, ...NAV_ITEMS[key], ...overrides }
}
